var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useState } from 'react';
import { Provider } from 'root/src/shared/@types/providers';
import { loginWith } from 'root/src/client/v2/auth/helpers/loginWith';
import { getReadableProviderName } from 'root/src/client/v2/auth/helpers/providerHelpers';
import { useAuthentication } from 'root/src/client/v2/auth/hooks/useAuthentication';
import { useLocation } from 'root/src/client/v2/browser/hooks/useLocation';
import { useQueryParam } from 'use-query-params';
import { StringQueryParam } from 'root/src/client/v2/queryParams/helpers/params.helper';
import { z } from 'zod';
import { useScopeSet } from 'root/src/client/v2/auth/hooks/useScopeSet';
import { getOidcUrl } from 'root/src/client/v2/auth/helpers/getOidcUrl';
import { routeUrls } from 'root/src/shared/descriptions/routes/routeUrls';
import { AuthBroadcastEvent, getAuthBroadcastChannel, } from 'root/src/client/web/auth/fixtures/auth.fixtures';
export const providerSearchValidatorSchema = z.union([
    z.literal(Provider.Twitch),
    z.literal(Provider.Google),
    z.literal(Provider.TikTok),
]);
// TODO centralize oauth to this fn
export const useSignInWithProvider = (params) => {
    const [isLoading, setIsLoading] = useState(false);
    const [providerSearchParam] = useQueryParam('provider', StringQueryParam({
        validator: providerSearchValidatorSchema,
    }));
    const { authentication } = useAuthentication();
    const provider = params.provider || providerSearchParam;
    if (!provider) {
        throw new Error('Provider is required');
    }
    const scopeSet = useScopeSet({ provider, scopeSet: params.scopeSet });
    const providerLabel = getReadableProviderName(provider);
    const { pathname } = useLocation();
    const state = {
        pathname: params.popup ? routeUrls.authPopupCallback() : pathname,
        scopeSet,
        popup: params.popup,
    };
    const onPopupSignIn = (event) => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b, _c;
        if (!params.popup) {
            throw new Error('Popup must be true for popup sign in');
        }
        const broadcastChannel = getAuthBroadcastChannel();
        const token = authentication === null || authentication === void 0 ? void 0 : authentication.accessToken;
        const url = getOidcUrl(provider, params.isLinking ? token : undefined, state);
        const width = 540;
        const height = 600;
        const left = event.screenX - (width / 2);
        const top = event.screenY - (height / 2);
        const popup = window.open(url, 'auth', `width=${width},height=${height},left=${left},top=${top}`);
        if (!popup) {
            if ((_a = params.popupOptions) === null || _a === void 0 ? void 0 : _a.onError) {
                yield params.popupOptions.onError(new Error('Could not open popup, please check your browser settings'));
            }
            return;
        }
        try {
            yield new Promise((resolve, reject) => {
                const checkedClose = setInterval(() => {
                    if (popup.closed) {
                        cleanup();
                        reject(new Error('OAuth popup closed'));
                    }
                }, 1000);
                const cleanup = () => {
                    broadcastChannel.close();
                    clearInterval(checkedClose);
                    if (!popup.closed)
                        popup.close();
                };
                broadcastChannel.addEventListener('message', (messageEvent) => __awaiter(void 0, void 0, void 0, function* () {
                    const { type } = messageEvent.data;
                    switch (type) {
                        case AuthBroadcastEvent.AUTH_SUCCESS: {
                            // The popup's OIDC callback set the auth cookies server-side (Set-Cookie), so no
                            // client `setCookie` ran and no `cookiechange` fired in this opener tab. Dispatch it
                            // here so `useAuthentication` re-reads the now-present cookies and the navbar, tRPC and
                            // cached queries pick up the new session reactively (the same channel the email sign-in
                            // uses), even before the caller's onSuccess (for example a reload) runs.
                            window.dispatchEvent(new Event('cookiechange'));
                            cleanup();
                            resolve(undefined);
                            break;
                        }
                        case AuthBroadcastEvent.AUTH_ERROR: {
                            console.error(messageEvent);
                            const { message } = messageEvent.data;
                            cleanup();
                            reject(new Error(message !== null && message !== void 0 ? message : 'OAuth error'));
                            break;
                        }
                        default:
                            reject(new Error('Unknown message type'));
                            cleanup();
                            break;
                    }
                }));
                broadcastChannel.addEventListener('messageerror', (messageEvent) => __awaiter(void 0, void 0, void 0, function* () {
                    console.error(messageEvent);
                    cleanup();
                    reject(new Error('OAuth message error'));
                }));
            });
            if ((_b = params.popupOptions) === null || _b === void 0 ? void 0 : _b.onSuccess) {
                yield params.popupOptions.onSuccess();
            }
        }
        catch (err) {
            console.error(err);
            if ((_c = params.popupOptions) === null || _c === void 0 ? void 0 : _c.onError) {
                yield params.popupOptions.onError(err);
            }
        }
    });
    const onSignIn = (event) => __awaiter(void 0, void 0, void 0, function* () {
        setIsLoading(true);
        const token = authentication === null || authentication === void 0 ? void 0 : authentication.accessToken;
        if (params.isLinking && !token) {
            setIsLoading(false);
            throw new Error('Make sure you are logged in');
        }
        if (params.popup) {
            yield onPopupSignIn(event);
            setIsLoading(false);
            return;
        }
        if (params.isLinking) {
            yield loginWith(provider)(state)(token);
            setIsLoading(false);
            return;
        }
        yield loginWith(provider)(state)();
        setIsLoading(false);
    });
    return {
        providerLabel,
        onSignIn,
        provider,
        isLoading,
        setIsLoading,
    };
};
